import React, { useMemo } from 'react';
import { Input, List, Tag, Typography, Empty, Select, Space, Button, Tooltip } from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  WifiOutlined,
  ThunderboltOutlined,
  ReloadOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { Lead, User } from '../../services/api';
import { getLeadCategoryStyle } from '../../utils/leadCategoryColors';
import {
  workspaceSurface,
  workspaceInset,
  workspaceListSelected,
  workspaceAccent,
} from './workspaceTheme';

const { Text } = Typography;
const { Option } = Select;

export type WorkspaceFilterSection = 'all' | 'entrada' | 'feedback' | 'registro' | 'instalados';
export type WorkspaceFilterArea = 'all' | 'telecommunications' | 'energy_solutions';

export interface WorkspaceFilters {
  search: string;
  section: WorkspaceFilterSection;
  area: WorkspaceFilterArea;
}

interface LeadListProps {
  leads: Lead[];
  loading: boolean;
  selectedLeadId?: number | null;
  /** Pass `null` to clear selection (same lead click or header control). */
  onSelectLead: (lead: Lead | null) => void;
  onCreateLead: () => void;
  onRefresh: () => void;
  filters: WorkspaceFilters;
  onFiltersChange: (filters: WorkspaceFilters) => void;
  /** Admin-only: view another user's workboard / client list. */
  adminViewAsUserId?: number | null;
  adminViewAsUsers?: User[];
  onAdminViewAsUserChange?: (userId: number) => void;
  loadingAdminUsers?: boolean;
}

const sectionColors: Record<string, string> = {
  entrada: 'blue',
  feedback: 'gold',
  registro: 'purple',
  instalados: 'green',
};

const LeadList: React.FC<LeadListProps> = ({
  leads,
  loading,
  selectedLeadId,
  onSelectLead,
  onCreateLead,
  onRefresh,
  filters,
  onFiltersChange,
  adminViewAsUserId,
  adminViewAsUsers,
  onAdminViewAsUserChange,
  loadingAdminUsers = false,
}) => {
  const filteredLeads = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    return leads.filter((lead) => {
      if (filters.section !== 'all' && lead.section !== filters.section) return false;
      if (filters.area !== 'all') {
        const area = Array.isArray(lead.business_area)
          ? (lead.business_area as unknown as string[]).join(',')
          : lead.business_area || '';
        if (!area.includes(filters.area)) return false;
      }
      if (!search) return true;
      return (
        lead.name?.toLowerCase().includes(search) ||
        lead.email?.toLowerCase().includes(search) ||
        lead.phone?.toLowerCase().includes(search) ||
        lead.company?.toLowerCase().includes(search) ||
        lead.dif?.toLowerCase().includes(search) ||
        lead.city?.toLowerCase().includes(search)
      );
    });
  }, [leads, filters]);

  const update = (patch: Partial<WorkspaceFilters>) =>
    onFiltersChange({ ...filters, ...patch });

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        ...workspaceSurface,
      }}
    >
      <div style={{ ...workspaceInset, padding: 16 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <Text strong style={{ fontSize: 16, color: '#0f172a', letterSpacing: '-0.02em' }}>
            Clientes ({filteredLeads.length})
          </Text>
          <Space size={4}>
            {selectedLeadId != null && (
              <Tooltip title="Desmarcar cliente">
                <Button
                  size="small"
                  type="text"
                  icon={<CloseCircleOutlined />}
                  onClick={() => onSelectLead(null)}
                  aria-label="Desmarcar cliente"
                />
              </Tooltip>
            )}
            <Tooltip title="Atualizar">
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={onRefresh}
                loading={loading}
              />
            </Tooltip>
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={onCreateLead}
            >
              Novo
            </Button>
          </Space>
        </div>
        {adminViewAsUsers && adminViewAsUsers.length > 0 && onAdminViewAsUserChange ? (
          <div style={{ marginBottom: 10 }}>
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
              Workboard de
            </Text>
            <Select
              size="small"
              style={{ width: '100%' }}
              value={adminViewAsUserId ?? undefined}
              onChange={onAdminViewAsUserChange}
              loading={loadingAdminUsers}
              disabled={loadingAdminUsers}
              showSearch
              optionFilterProp="children"
            >
              {adminViewAsUsers.map((u) => (
                <Option key={u.id} value={u.id}>
                  {(u as User & { display_name?: string }).display_name || u.username}
                </Option>
              ))}
            </Select>
          </div>
        ) : null}
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Pesquisar nome, NIF, email..."
          value={filters.search}
          onChange={(e) => update({ search: e.target.value })}
        />
        <Space size={8} style={{ marginTop: 8, width: '100%' }} wrap>
          <Select
            size="small"
            value={filters.section}
            style={{ minWidth: 110 }}
            onChange={(v) => update({ section: v })}
          >
            <Option value="all">Todas as etapas</Option>
            <Option value="entrada">Entrada</Option>
            <Option value="feedback">Feedback</Option>
            <Option value="registro">Registo</Option>
            <Option value="instalados">Instalados</Option>
          </Select>
          <Select
            size="small"
            value={filters.area}
            style={{ minWidth: 130 }}
            onChange={(v) => update({ area: v })}
          >
            <Option value="all">Todas as áreas</Option>
            <Option value="telecommunications">Telecom</Option>
            <Option value="energy_solutions">Energia</Option>
          </Select>
        </Space>
      </div>
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          background: 'linear-gradient(180deg, #fafbfd 0%, #f5f7fb 100%)',
        }}
      >
        {filteredLeads.length === 0 ? (
          <div style={{ padding: 32 }}>
            <Empty description="Nenhum lead encontrado" />
          </div>
        ) : (
          <List
            dataSource={filteredLeads}
            loading={loading}
            renderItem={(lead) => {
              const isSelected = lead.id === selectedLeadId;
              const area = Array.isArray(lead.business_area)
                ? (lead.business_area as unknown as string[]).join(',')
                : lead.business_area || '';
              const isTelecom = area.includes('telecommunications');
              return (
                <Tooltip
                  key={lead.id}
                  title={isSelected ? 'Clique novamente para desmarcar' : undefined}
                >
                  <List.Item
                    onClick={() => onSelectLead(isSelected ? null : lead)}
                    style={{
                      cursor: 'pointer',
                      padding: '12px 16px',
                      borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
                      transition: 'background 0.15s ease',
                      ...(isSelected ? workspaceListSelected : { borderLeft: '4px solid transparent' }),
                    }}
                  >
                  <List.Item.Meta
                    title={
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <span style={getLeadCategoryStyle(lead.lead_category)}>
                          {lead.name}
                        </span>
                        {isTelecom ? (
                          <WifiOutlined style={{ color: workspaceAccent }} />
                        ) : (
                          <ThunderboltOutlined style={{ color: '#b45309' }} />
                        )}
                      </div>
                    }
                    description={
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {lead.phone || lead.email || lead.dif || 'Sem contacto'}
                        </Text>
                        <Space size={4} wrap>
                          {lead.section && (
                            <Tag color={sectionColors[lead.section] || 'default'}>
                              {lead.section}
                            </Tag>
                          )}
                          {lead.lead_status && (
                            <Tag>{lead.lead_status}</Tag>
                          )}
                        </Space>
                      </Space>
                    }
                  />
                </List.Item>
                </Tooltip>
              );
            }}
          />
        )}
      </div>
    </div>
  );
};

export default LeadList;
